import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorText } from "@/lib/api";
import { dateTime } from "@/lib/format";
import { Search, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const PLAN_LABELS = { free: "Free", pro_monthly: "Pro monthly", pro_yearly: "Pro yearly" };

function DeleteUser({ user, onDeleted }) {
  if (user.role === "admin") return <span className="text-xs text-slate-500" data-testid={`user-protected-${user.id}`}>Protected</span>;
  const remove = async () => {
    try {
      await api.delete(`/admin/users/${user.id}`);
      toast.success(`${user.email} deleted`);
      onDeleted();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className="dpa-del" aria-label={`Delete ${user.email}`} data-testid={`user-delete-${user.id}`}><Trash2 size={15} /></button>
      </AlertDialogTrigger>
      <AlertDialogContent className="ln-dialog" data-testid="user-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this account?</AlertDialogTitle>
          <AlertDialogDescription>{user.email} will lose access immediately. This can't be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="user-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-500" data-testid="user-delete-confirm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const shown = term ? users.filter((u) => u.email.toLowerCase().includes(term) || (u.name || "").toLowerCase().includes(term)) : users;
  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const setPlan = async (u, plan) => {
    try {
      await api.post(`/admin/users/${u.id}/plan`, { plan });
      toast.success(`${u.email} → ${PLAN_LABELS[plan]}`);
      load();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <article className="dpa-panel dpa-history" data-testid="users-panel">
      <div className="dpa-users-head">
        <h2>Users &amp; plans <small data-testid="users-count">{term ? `${shown.length} of ${users.length}` : users.length}</small></h2>
        <div className="dpa-search">
          <Search size={15} />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or name" className="h-9 pl-8 bg-slate-900/70" data-testid="users-search-input" />
        </div>
      </div>
      <div className="dpa-table-scroll">
        <table>
          <thead><tr>{["Email", "Name", "Role", "Joined", "Plan", ""].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`}>
                <td>{u.email}</td>
                <td>{u.name}</td>
                <td><span className="dpa-type">{u.role}</span></td>
                <td className="whitespace-nowrap">{u.created_at ? dateTime(u.created_at) : "—"}</td>
                <td>
                  <Select value={u.plan} onValueChange={(v) => setPlan(u, v)}>
                    <SelectTrigger className="w-36 h-8 bg-slate-900/70" data-testid={`user-plan-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PLAN_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td><DeleteUser user={u} onDeleted={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!shown.length && <p className="dpa-empty" data-testid="users-empty">{term ? `No accounts match "${q.trim()}".` : "No users yet."}</p>}
    </article>
  );
}
